import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, MessageCircle, Check, CreditCard, ExternalLink } from "lucide-react";
import { API, useAuth } from "@/App";

function formatErr(detail) {
  if (!detail) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail?.msg || detail);
}

const brl = (n) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });

export default function ReserveDialog({ model, onClose, onSuccess, onError }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState("");
  const [loadingKind, setLoadingKind] = useState(""); // "stripe" | "mp"
  const [error, setError] = useState("");
  const [mpLink, setMpLink] = useState("");
  const [whatsapp, setWhatsapp] = useState("5547999711358");
  const [mpDone, setMpDone] = useState(null); // when set, shows the "success step" for MP flow

  const deposit = model.deposit_amount ?? model.price * 0.2;

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => {
      setMpLink(data.mercadopago_link || "");
      setWhatsapp(data.contact_whatsapp || "5547999711358");
    }).catch(() => {});
  }, []);

  const submitStripe = async (e) => {
    e.preventDefault();
    setLoadingKind("stripe");
    setError("");
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        model_slug: model.slug,
        notes,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const msg = formatErr(err.response?.data?.detail);
      setError(msg);
      onError?.(msg);
      setLoadingKind("");
    }
  };

  const submitMercadoPago = async () => {
    if (!mpLink) return;
    setLoadingKind("mp");
    setError("");
    try {
      await axios.post(`${API}/reservations/manual`, {
        model_slug: model.slug,
        method: "mercadopago",
        notes,
      });
      const waNum = (whatsapp || "").replace(/\D/g, "") || "5547999711358";
      const waMsg = `Olá! Reservei a ${model.name} e vou pagar o sinal de ${brl(deposit)} pelo Mercado Pago. Assim que finalizar o pagamento, envio o comprovante aqui.`;
      const waUrl = `https://wa.me/${waNum}?text=${encodeURIComponent(waMsg)}`;
      setMpDone({ mpLink, waUrl });
    } catch (err) {
      const msg = formatErr(err.response?.data?.detail);
      setError(msg);
      onError?.(msg);
    } finally {
      setLoadingKind("");
    }
  };

  const loading = loadingKind !== "";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reserve-dialog"
    >
      <div
        className="pm-card pm-glow-ring w-full max-w-lg p-8 relative max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={onClose} data-testid="reserve-dialog-close">
          <X size={20} />
        </button>

        {mpDone ? (
          <div data-testid="reserve-mp-success">
            <div className="pm-label mb-2">// Reserva registrada</div>
            <h3 className="font-display text-2xl font-black">
              Sua unidade da <span className="pm-neon-text">{model.name}</span> foi reservada.
            </h3>
            <p className="text-white/70 mt-3 text-sm">
              Agora faltam 2 passos rápidos para confirmarmos o sinal de <b className="text-white">{brl(deposit)}</b>:
            </p>
            <div className="mt-5 space-y-3">
              <a
                href={mpDone.mpLink}
                target="_blank"
                rel="noreferrer"
                className="pm-btn-primary w-full text-xs flex items-center justify-center gap-2"
                data-testid="mp-open-link"
              >
                <ExternalLink size={14} /> 1. Abrir Mercado Pago e pagar {brl(deposit)}
              </a>
              <a
                href={mpDone.waUrl}
                target="_blank"
                rel="noreferrer"
                className="pm-btn-ghost w-full text-xs flex items-center justify-center gap-2 border-emerald-500/40 hover:border-emerald-400 hover:text-emerald-300"
                data-testid="mp-open-whatsapp"
              >
                <MessageCircle size={14} /> 2. Enviar comprovante no WhatsApp
              </a>
            </div>
            <div className="mt-5 text-[11px] text-white/50 leading-relaxed">
              Sua reserva já está registrada no nosso sistema. Após recebermos o comprovante,
              confirmamos e enviamos o contrato do pré-lançamento por e-mail.
            </div>
            <button className="pm-btn-ghost w-full text-xs mt-5" onClick={onClose} data-testid="mp-close-button">
              Fechar
            </button>
          </div>
        ) : (
        <>
        <div className="pm-label mb-2">// Reservar unidade</div>
        <h3 className="font-display text-2xl font-black">{model.name}</h3>
        <div className="mt-1 text-white/60 text-sm">
          Valor total: <span className="text-white font-mono">{brl(model.price)}</span>
        </div>

        <div className="mt-5 pm-card pm-glow-ring p-4">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="pm-label text-[10px]">Sinal (20%)</div>
              <div className="font-mono text-3xl pm-neon-text mt-1">{brl(deposit)}</div>
            </div>
            <div className="text-right text-xs text-white/50 max-w-[150px]">
              80% restantes conforme o cronograma de fabricação.
            </div>
          </div>
        </div>

        <div className="mt-4 pm-card p-3 bg-[color:var(--pm-surface-2)]">
          <div className="text-xs text-white/60">
            Reservando como: <span className="text-white font-mono">{user?.name}</span>
            <br /> {user?.email} · {user?.phone}
          </div>
        </div>

        <form onSubmit={submitStripe} className="mt-4 space-y-3">
          <textarea
            className="pm-input min-h-[70px] resize-y"
            placeholder="Observações (opcional): finalidade, localidade, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="reserve-notes-input"
          />
          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="reserve-error">
              {error}
            </div>
          )}
          <ul className="text-xs text-white/60 space-y-2 pt-1">
            <li className="flex gap-2"><Check size={14} className="text-[color:var(--pm-neon)] shrink-0 mt-0.5" /> Sua unidade fica reservada nas 30 do pré-lançamento.</li>
            <li className="flex gap-2"><Check size={14} className="text-[color:var(--pm-neon)] shrink-0 mt-0.5" /> Entrega estimada em 6 meses + 2 anos de melhorias.</li>
          </ul>

          <button
            type="submit"
            className="pm-btn-primary w-full text-xs mt-3 flex items-center justify-center gap-2"
            disabled={loading}
            data-testid="reserve-submit-button"
          >
            {loadingKind === "stripe" ? <Loader2 size={14} className="animate-spin" /> : (
              <>
                <CreditCard size={14} /> Pagar sinal de {brl(deposit)} · Cartão (Stripe)
              </>
            )}
          </button>

          {mpLink && (
            <>
              <div className="flex items-center gap-3 text-[11px] text-white/40 my-2">
                <div className="flex-1 h-px bg-white/10" />
                <span>ou</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <button
                type="button"
                onClick={submitMercadoPago}
                className="pm-btn-ghost w-full text-xs flex items-center justify-center gap-2 border-[#00b1ea]/40 hover:border-[#00b1ea] hover:text-[#00b1ea]"
                disabled={loading}
                data-testid="reserve-mp-button"
              >
                {loadingKind === "mp" ? <Loader2 size={14} className="animate-spin" /> : (
                  <>
                    <ExternalLink size={14} /> Pagar via Mercado Pago (PIX / Boleto)
                  </>
                )}
              </button>
              <div className="text-[10px] text-white/40 text-center flex items-center justify-center gap-1">
                <MessageCircle size={11} /> Você será levado ao Mercado Pago e ao WhatsApp para enviar o comprovante.
              </div>
            </>
          )}
        </form>
        </>
        )}
      </div>
    </div>
  );
}
